import { FC, createContext, useContext, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';

import { DataPaginateResponse } from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { DriverCompanyBase } from '~services/driver/types';
import { getDriverCompanyAvailables } from '~services/driver';
import { AuthExtendedCompany } from '~services/auth/types';
import { getAssociatedCompanies } from '~services/partnerCompany';
import {
  AdminReportUserSatisfaction,
  AdminReportUserSatisfactionFilters,
} from '~services/admin/report/types';
import { getReportUserSatisfaction } from '~services/admin/report';
import { selectAuth } from '~store/slices/auth/index';
import {
  useRequest,
  useTypePartnerCompany,
  useAppSelector,
  useSubscriptionFeatures,
} from '~hooks/index';

import { CURRENT_PERIOD_MONTH } from '../utils';

import { ReportsUserSatifactionSectionContextValue } from './types';
import { getPayloadGetReportUserSatisfactionData } from './utils';

const ReportsUserSatifactionSectionContext =
  createContext<ReportsUserSatifactionSectionContextValue>({
    filters: {
      currentPeriod: {
        value: CURRENT_PERIOD_MONTH,
        setValue: () => {},
      },
      currentDriverId: {
        value: null,
        setValue: () => {},
      },
      currentAssociatedCompanyId: {
        value: null,
        setValue: () => {},
      },
      selectsData: {
        availablesDrivers: {
          data: [],
          loading: true,
        },
        associatedCompanies: {
          data: [],
          loading: true,
        },
      },
    },
    data: {
      satisfaction: {
        data: null,
        loading: true,
      },
    },
  });

export const useReportsUserSatifactionSectionContext =
  (): ReportsUserSatifactionSectionContextValue =>
    useContext(ReportsUserSatifactionSectionContext);

const ReportsUserSatifactionSectionContextProvider: FC = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isCarrier } = useTypePartnerCompany();
  const { user } = useAppSelector(selectAuth);
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const [currentPeriod, setCurrentPeriod] =
    useState<Nullable<Date>>(CURRENT_PERIOD_MONTH);

  const [currentDriverId, setCurrentDriverId] =
    useState<Nullable<string>>(null);

  const [currentAssociatedCompanyId, setCurrentAssociatedCompanyId] = useState<
    Nullable<string>
  >(isCarrier ? user?.companyId ?? null : null);

  const [availablesDrivers, setAvailablesDrivers] = useState<
    DriverCompanyBase[]
  >([]);
  const [, loadingAvailablesDrivers] = useRequest({
    request: getDriverCompanyAvailables,
    payload: null,
    withPostSuccess: (response) => {
      const availablesDriversResponse = response.data as DataPaginateResponse<
        DriverCompanyBase[]
      >;

      setAvailablesDrivers(availablesDriversResponse.data.results);
    },
  });

  const [associatedCompanies, setAssociatedCompanies] = useState<
    AuthExtendedCompany[]
  >([]);
  const [, loadingAssocitedCompanies] = useRequest({
    request: getAssociatedCompanies,
    payload: null,
    withPostSuccess: (response) => {
      const associatedCompaniesResponse = response.data?.data
        .results as AuthExtendedCompany[];

      setAssociatedCompanies(associatedCompaniesResponse);
    },
  });

  const [userSatisfactionData, setUserSatisfactionData] =
    useState<Nullable<AdminReportUserSatisfaction>>(null);
  const payloadGetReportUserSatisfactionData =
    useMemo<AdminReportUserSatisfactionFilters>(
      () =>
        getPayloadGetReportUserSatisfactionData({
          currentPeriod,
          currentDriverId,
          currentAssociatedCompanyId,
        }),
      [currentPeriod, currentDriverId, currentAssociatedCompanyId],
    );
  const isValidToFetchUserSatisfaction = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.ReportCustomSatisfaction),
    [hasAccessToFeature],
  );
  const [, loadingUserSatisfaction] = useRequest(
    {
      initialState: null,
      request: getReportUserSatisfaction,
      payload: payloadGetReportUserSatisfactionData,
      initialLoading: isValidToFetchUserSatisfaction,
      isValidToRequest: isValidToFetchUserSatisfaction,
      withPostSuccess: (response) => {
        const userSatisfactionyResponse = response.data
          ?.data as AdminReportUserSatisfaction;

        setUserSatisfactionData(userSatisfactionyResponse);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
    },
    [payloadGetReportUserSatisfactionData],
  );

  return (
    <ReportsUserSatifactionSectionContext.Provider
      value={{
        filters: {
          currentPeriod: {
            value: currentPeriod,
            setValue: setCurrentPeriod,
          },
          currentDriverId: {
            value: currentDriverId,
            setValue: setCurrentDriverId,
          },
          currentAssociatedCompanyId: {
            value: currentAssociatedCompanyId,
            setValue: setCurrentAssociatedCompanyId,
          },
          selectsData: {
            availablesDrivers: {
              data: availablesDrivers,
              loading: loadingAvailablesDrivers,
            },
            associatedCompanies: {
              data: associatedCompanies,
              loading: loadingAssocitedCompanies,
            },
          },
        },
        data: {
          satisfaction: {
            data: userSatisfactionData,
            loading: loadingUserSatisfaction,
          },
        },
      }}
    >
      {children}
    </ReportsUserSatifactionSectionContext.Provider>
  );
};

export default ReportsUserSatifactionSectionContextProvider;
